import { Recycle, Layers, Factory, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { MaskedLine, Reveal, SectionNumber } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const KPIS = [
    {
        icon: Recycle,
        value: "100%",
        label: "Landfill diversion pathways",
        desc: "Food-waste treatment routes engineered for complete diversion from landfill.",
    },
    {
        icon: Layers,
        value: "4",
        label: "Integrated domains",
        desc: "Waste operations, valorization, sourcing and advanced materials under one partner.",
    },
    {
        icon: Factory,
        value: "98%",
        label: "Recovered steel purity",
        desc: "Smelting-grade steel powder recovered from end-of-life tires.",
    },
    {
        icon: ShieldCheck,
        value: "ISCC",
        label: "Certified supply chains",
        desc: "Traceable, sustainability-compliant UCO and feedstock supply programmes.",
    },
];

const SCORES = [
    { label: "Landfill Diversion", target: 90 },
    { label: "Cost Optimization", target: 85 },
    { label: "Compliance & Reporting", target: 95 },
    { label: "Supply Traceability", target: 100 },
];

export const Impact = () => (
    <section
        id="impact"
        data-testid="impact-section"
        className="relative bg-[#F0F5EA] py-28 md:py-40"
    >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <SectionNumber n="05" label="Sustainability Impact" />
            <div className="mb-16 flex flex-wrap items-end justify-between gap-8">
                <h2 className="font-display max-w-2xl text-5xl font-light leading-[1.06] tracking-tighter text-[#0E1112] sm:text-6xl lg:text-7xl">
                    <MaskedLine>Impact you</MaskedLine>
                    <MaskedLine delay={0.15} className="text-[#C8102E]">
                        can measure.
                    </MaskedLine>
                </h2>
                <Reveal delay={0.2} className="max-w-sm">
                    <p className="text-sm leading-relaxed text-[#45543F]">
                        Every NOMAS programme is built around defined
                        environmental and commercial KPIs — reported, audited
                        and improved over time.
                    </p>
                </Reveal>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {KPIS.map((k, i) => (
                    <Reveal key={k.label} delay={i * 0.08} className="h-full">
                        <div
                            data-testid={`impact-kpi-${i + 1}`}
                            className="group flex h-full flex-col rounded-2xl border border-[#DCE5D4] bg-white p-8 transition-[border-color,transform] duration-400 hover:-translate-y-1.5 hover:border-[#598B3B]/50"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#598B3B]/10 text-[#598B3B]">
                                <k.icon size={20} strokeWidth={1.6} />
                            </span>
                            <p className="font-display mt-8 text-5xl font-light tracking-tight text-[#598B3B]">
                                {k.value}
                            </p>
                            <p className="font-display mt-3 text-lg font-medium tracking-tight text-[#0E1112]">
                                {k.label}
                            </p>
                            <p className="mt-2.5 text-[13px] leading-relaxed text-[#5C6B57]">
                                {k.desc}
                            </p>
                            <span className="mt-auto block h-0.5 w-8 bg-[#7CBF52] transition-[width] duration-500 group-hover:w-full" />
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.15}>
                <div
                    data-testid="client-benefits"
                    className="mt-16 grid gap-10 rounded-3xl border border-[#DCE5D4] bg-white p-8 md:p-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16"
                >
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#C8102E]">
                            Client Benefits
                        </p>
                        <h3 className="font-display mt-3 text-2xl font-light leading-snug tracking-tight text-[#0E1112] md:text-3xl">
                            Target achievement, scored and reported
                        </h3>
                        <p className="mt-4 text-sm leading-relaxed text-[#45543F]">
                            Every engagement defines target scores up front.
                            Progress is tracked against each target and shared
                            with your team — so value is visible, not
                            promised.
                        </p>
                    </div>
                    <div className="flex flex-col justify-center gap-7">
                        {SCORES.map((s, i) => (
                            <div key={s.label} data-testid={`benefit-score-${i + 1}`}>
                                <div className="mb-2 flex items-baseline justify-between">
                                    <span className="text-sm font-medium text-[#0E1112]">
                                        {s.label}
                                    </span>
                                    <span className="font-display text-sm font-semibold text-[#598B3B]">
                                        {s.target}%
                                    </span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#EDF2E6]">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${s.target}%` }}
                                        viewport={{ once: true, margin: "-60px" }}
                                        transition={{
                                            duration: 1.4,
                                            delay: 0.15 + i * 0.12,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                        className="h-full rounded-full bg-gradient-to-r from-[#598B3B] to-[#7CBF52]"
                                    />
                                </div>
                            </div>
                        ))}
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#8CA093]">
                            Typical engagement targets — agreed per programme
                        </p>
                    </div>
                </div>
            </Reveal>
            <Reveal delay={0.15}>
                <div className="mt-16 flex flex-wrap items-center justify-center gap-4" data-testid="impact-cta">
                    <button
                        onClick={() => scrollToSection("#contact")}
                        data-testid="impact-cta-talk"
                        className="rounded-full bg-[#598B3B] px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#4a7530]"
                    >
                        Talk to an Expert
                    </button>
                    <a
                        href="mailto:gm@nomas-co.com?subject=Solution%20Enquiry"
                        data-testid="impact-cta-quote"
                        className="rounded-full border border-[#598B3B]/60 px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E1112] transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#598B3B] hover:bg-[#598B3B]/10"
                    >
                        Get a Quote
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);
